'use client';

import { c as _c } from "react/compiler-runtime";
import type { SavedViewSummary } from '@repo/schema/table-configuration';
import { type SavedViewStart, useSavedViewsPanel, useTableConfiguration, useTableConfigurationSnapshot, type ViewPageSection } from '@repo/table/table-configuration';
import { useDebouncedValue } from '@repo/tanstack/pacer';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Input, Label, Skeleton } from '@repo/ui/components';
import { useSidebar } from '@repo/ui/components/ui/sidebar';
import { ArrowLeft, MoreHorizontal, Pencil, Pin, Plus, Trash2, X } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { productError } from './product-error.ts';
import { ViewDetailsForm } from './view-details-form.tsx';
import { type Audience, AudiencePicker, toAudienceInput } from './view-sharing.tsx';
import { DeleteView, ManageView } from './manage-view.tsx';
import { defaultViewName, organisationLabel, ownerLabel } from './saved-view-format.ts';
import { SavedViewIcon } from './saved-view-icon.tsx';
import { useSavedViewSection } from './use-saved-view-section.ts';

/** The Browse views button carries this, so pressing it toggles rather than closes and reopens. */
export const SAVED_VIEWS_TRIGGER = 'data-saved-views-trigger';
type Actions = {
  isPinned: (_view: SavedViewSummary) => boolean;
  open: (_id: null | number) => void;
  togglePin: (_view: SavedViewSummary) => Promise<void>;
};
const PinToggle = t0 => {
  const $ = _c(21);
  const {
    actions,
    view
  } = t0;
  const [pending, setPending] = useState(null);
  let t1;
  if ($[0] !== actions || $[1] !== pending || $[2] !== view) {
    t1 = pending ?? actions.isPinned(view);
    $[0] = actions;
    $[1] = pending;
    $[2] = view;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const pinned = t1;
  const t2 = pinned ? `Unpin ${view.name}` : `Pin ${view.name}`;
  const t3 = !pinned && "opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("shrink-0 text-muted-foreground", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = pending !== null;
  let t6;
  if ($[6] !== actions || $[7] !== pinned || $[8] !== view) {
    t6 = () => {
      setPending(!pinned);
      actions.togglePin(view).finally(() => setPending(null));
    };
    $[6] = actions;
    $[7] = pinned;
    $[8] = view;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = pinned && "fill-current";
  let t8;
  if ($[10] !== t7) {
    t8 = cn(t7);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <Pin className={t8} data-sentry-element="Pin" data-sentry-source-file="saved-views-panel.tsx" />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== pinned || $[15] !== t2 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t9) {
    t10 = <Button aria-label={t2} aria-pressed={pinned} className={t4} disabled={t5} onClick={t6} size="icon-xs" variant="ghost" data-sentry-element="Button" data-sentry-component="PinToggle" data-sentry-source-file="saved-views-panel.tsx">{t9}</Button>;
    $[14] = pinned;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
const RowMenu = t0 => {
  const $ = _c(23);
  const {
    view
  } = t0;
  const panel = useSavedViewsPanel();
  let t1;
  if ($[0] !== panel || $[1] !== view) {
    t1 = section => () => panel.open(view, section);
    $[0] = panel;
    $[1] = view;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const page = t1;
  const t2 = `More for ${view.name}`;
  let t3;
  if ($[3] !== t2) {
    t3 = <Button aria-label={t2} className="shrink-0 text-muted-foreground opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100" size="icon-xs" variant="ghost" />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MoreHorizontal data-sentry-element="MoreHorizontal" data-sentry-source-file="saved-views-panel.tsx" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <DropdownMenuTrigger render={t3} data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="saved-views-panel.tsx">{t4}</DropdownMenuTrigger>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== page) {
    t6 = page();
    $[8] = page;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Pencil data-sentry-element="Pencil" data-sentry-source-file="saved-views-panel.tsx" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = view.is_owner ? "Edit view\u2026" : "View details\u2026";
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <DropdownMenuItem onClick={t6} data-sentry-element="DropdownMenuItem" data-sentry-source-file="saved-views-panel.tsx">{t7} {t8}</DropdownMenuItem>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== page || $[15] !== view.is_owner) {
    t10 = view.is_owner ? <><DropdownMenuSeparator /><DropdownMenuItem onClick={page("Delete")} variant="destructive"><Trash2 /> Delete…</DropdownMenuItem></> : null;
    $[14] = page;
    $[15] = view.is_owner;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <DropdownMenuContent align="start" className="min-w-48" side="right" data-sentry-element="DropdownMenuContent" data-sentry-source-file="saved-views-panel.tsx">{t9}{t10}</DropdownMenuContent>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5) {
    t12 = <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-component="RowMenu" data-sentry-source-file="saved-views-panel.tsx">{t5}{t11}</DropdownMenu>;
    $[20] = t11;
    $[21] = t5;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};
type SectionProps = {
  access: 'owned' | 'shared';
  actions: Actions;
  openId: null | number;
  search: string;
  title: string;
};
const Section = t0 => {
  const $ = _c(38);
  const {
    access,
    actions,
    openId,
    search,
    title
  } = t0;
  const panel = useSavedViewsPanel();
  let t1;
  if ($[0] !== access || $[1] !== search) {
    t1 = {
      access,
      enabled: true,
      search
    };
    $[0] = access;
    $[1] = search;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    items,
    refetch
  } = useSavedViewSection(t1);
  let t2;
  if ($[3] !== title) {
    t2 = <h3 className="px-2 pt-3 pb-1 text-xs font-semibold text-muted-foreground">{title}</h3>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isPending) {
    t3 = isPending ? <Skeleton className="mx-2 h-7" /> : null;
    $[5] = isPending;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== error || $[8] !== refetch) {
    t4 = error ? <p className="flex items-center gap-2 px-2 text-xs text-destructive" role="alert">Couldn’t load these views.<Button className="h-auto p-0 text-xs" onClick={() => void refetch()} variant="link">Retry</Button></p> : null;
    $[7] = error;
    $[8] = refetch;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== error || $[11] !== isPending || $[12] !== items.length || $[13] !== search) {
    t5 = !isPending && !error && items.length === 0 ? <p className="px-2 text-xs text-muted-foreground">{search ? "No views match." : "No views yet."}</p> : null;
    $[10] = error;
    $[11] = isPending;
    $[12] = items.length;
    $[13] = search;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== access || $[16] !== actions || $[17] !== items || $[18] !== openId || $[19] !== panel) {
    let t7;
    if ($[21] !== access || $[22] !== actions || $[23] !== openId || $[24] !== panel) {
      t7 = item => <div className={cn("group/row flex items-center gap-1 rounded pr-1 hover:bg-muted", openId === item.id && "bg-muted")} key={item.id}><button aria-current={openId === item.id ? "page" : undefined} className={cn("flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm", !item.available && "text-muted-foreground")} onClick={() => item.available ? actions.open(item.id) : panel.open(item)} type="button"><SavedViewIcon icon={item.icon} /><span className="min-w-0 flex-1 truncate">{item.name}</span>{access === "shared" ? <span className="max-w-24 truncate text-xs text-muted-foreground">{ownerLabel(item)}</span> : null}</button><PinToggle actions={actions} view={item} /><RowMenu view={item} /></div>;
      $[21] = access;
      $[22] = actions;
      $[23] = openId;
      $[24] = panel;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    t6 = items.map(t7);
    $[15] = access;
    $[16] = actions;
    $[17] = items;
    $[18] = openId;
    $[19] = panel;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[26] !== fetchNextPage || $[27] !== hasNextPage || $[28] !== isFetchingNextPage) {
    t7 = hasNextPage ? <Button className="mx-2 justify-start text-xs" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()} size="sm" variant="ghost">Load more</Button> : null;
    $[26] = fetchNextPage;
    $[27] = hasNextPage;
    $[28] = isFetchingNextPage;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t2 || $[31] !== t3 || $[32] !== t4 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7 || $[36] !== title) {
    t8 = <section aria-label={title} className="flex flex-col gap-0.5" data-sentry-component="Section" data-sentry-source-file="saved-views-panel.tsx">{t2}{t3}{t4}{t5}{t6}{t7}</section>;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = title;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  return t8;
};
const ViewPage = t0 => {
  const $ = _c(48);
  const {
    actions,
    section,
    view
  } = t0;
  const panel = useSavedViewsPanel();
  const {
    view: openView
  } = useTableConfigurationSnapshot();
  const isOpen = openView?.id === view.id;
  let t1;
  if ($[0] !== panel) {
    t1 = () => panel.open();
    $[0] = panel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowLeft data-sentry-element="ArrowLeft" data-sentry-source-file="saved-views-panel.tsx" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <Button aria-label="Back to views" onClick={t1} size="icon-sm" variant="ghost" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t2}</Button>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== view.icon) {
    t4 = <SavedViewIcon icon={view.icon} data-sentry-element="SavedViewIcon" data-sentry-source-file="saved-views-panel.tsx" />;
    $[5] = view.icon;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== view.name) {
    t5 = <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{view.name}</h2>;
    $[7] = view.name;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== actions || $[10] !== view) {
    t6 = <PinToggle actions={actions} view={view} data-sentry-element="PinToggle" data-sentry-source-file="saved-views-panel.tsx" />;
    $[9] = actions;
    $[10] = view;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = !view.available || isOpen;
  let t8;
  if ($[12] !== actions || $[13] !== view.id) {
    t8 = () => actions.open(view.id);
    $[12] = actions;
    $[13] = view.id;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = isOpen ? "Showing" : "Open";
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <Button disabled={t7} onClick={t8} size="sm" variant="outline" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t9}</Button>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <X data-sentry-element="X" data-sentry-source-file="saved-views-panel.tsx" />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== panel.close) {
    t12 = <Button aria-label="Close Saved Views" onClick={panel.close} size="icon-sm" variant="ghost" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t11}</Button>;
    $[20] = panel.close;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6) {
    t13 = <header className="flex items-center gap-1 border-b p-2">{t3}{t4}{t5}{t6}{t10}{t12}</header>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== view) {
    t14 = view.available ? null : <p className="border-b p-3 text-xs text-muted-foreground">Not available in the current Organisation. It applies to {organisationLabel(view)}.</p>;
    $[29] = view;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== panel || $[32] !== view.id) {
    t15 = updated => panel.replace(view.id, updated);
    $[31] = panel;
    $[32] = view.id;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15 || $[35] !== view) {
    t16 = <ManageView key={view.id} onChange={t15} view={view} data-sentry-element="ManageView" data-sentry-source-file="saved-views-panel.tsx" />;
    $[34] = t15;
    $[35] = view;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t16) {
    t17 = <div className="min-h-0 flex-1 overflow-y-auto">{t14}{t16}</div>;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== panel || $[41] !== section || $[42] !== view) {
    t18 = view.is_owner ? <DeleteView confirm={section === "Delete"} key={`${view.id}-${section ?? ""}`} onDeleted={() => panel.replace(view.id, null)} view={view} /> : null;
    $[40] = panel;
    $[41] = section;
    $[42] = view;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t13 || $[45] !== t17 || $[46] !== t18) {
    t19 = <>{t13}{t17}{t18}</>;
    $[44] = t13;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
};
const NEW_VIEW_FORM = 'new-saved-view';
const NOTHING_NEW = 'new-saved-view-nothing-new';
const LOADING_COLUMNS = 'new-saved-view-loading-columns';
const START_OPTIONS: readonly {
  label: string;
  start: SavedViewStart;
}[] = [{
  label: 'Current table',
  start: 'current'
}, {
  label: 'Blank start',
  start: 'blank'
}];

/**
 * Saves a new view, shared from the start if chosen: the live filters and columns, or a Blank
 * start with only the locked columns and the default query.
 */
const NewViewPage = () => {
  const $ = _c(56);
  const panel = useSavedViewsPanel();
  const controller = useTableConfiguration();
  const {
    actions,
    view
  } = useTableConfigurationSnapshot();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mode: "owner",
      roles: [],
      users: []
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [audience, setAudience] = useState(t0);
  const [isSaving, setIsSaving] = useState(false);
  const [preferred, setPreferred] = useState("current");
  const canStartCurrent = actions.saveAsNew;
  const start = canStartCurrent ? preferred : "blank";
  const isLoadingColumns = start === "blank" && !actions.saveBlank;
  let t1;
  if ($[1] !== panel) {
    t1 = () => panel.open();
    $[1] = panel;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowLeft data-sentry-element="ArrowLeft" data-sentry-source-file="saved-views-panel.tsx" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <Button aria-label="Back to views" onClick={t1} size="icon-sm" variant="ghost" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t2}</Button>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h2 className="min-w-0 flex-1 truncate text-sm font-medium">New view</h2>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <X data-sentry-element="X" data-sentry-source-file="saved-views-panel.tsx" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== panel.close) {
    t6 = <Button aria-label="Close Saved Views" onClick={panel.close} size="icon-sm" variant="ghost" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t5}</Button>;
    $[8] = panel.close;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <header className="flex items-center gap-1 border-b p-2">{t3}{t4}{t6}</header>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label data-sentry-element="Label" data-sentry-source-file="saved-views-panel.tsx">Start from</Label>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== canStartCurrent || $[15] !== start) {
    t9 = START_OPTIONS.map(option => <Button aria-describedby={option.start === "current" && !canStartCurrent ? NOTHING_NEW : undefined} aria-pressed={start === option.start} disabled={option.start === "current" && !canStartCurrent} key={option.start} onClick={() => setPreferred(option.start)} size="sm" type="button" variant={start === option.start ? "default" : "outline"}>{option.label}</Button>);
    $[14] = canStartCurrent;
    $[15] = start;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <div aria-label="Start from" className="flex gap-1" role="group">{t9}</div>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = start === "current" ? "Saves the current filters and columns." : "Saves only the locked columns, with no filters.";
  let t12;
  if ($[19] !== t11) {
    t12 = <p className="text-sm text-muted-foreground">{t11}</p>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== canStartCurrent) {
    t13 = canStartCurrent ? null : <p className="text-xs text-muted-foreground" id={NOTHING_NEW}>Nothing new to save yet</p>;
    $[21] = canStartCurrent;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== canStartCurrent || $[24] !== controller.definition || $[25] !== start || $[26] !== view?.summary?.name) {
    t14 = start === "blank" && canStartCurrent ? <p className="text-xs text-muted-foreground">Unsaved changes to {view?.summary?.name ?? defaultViewName(controller.definition)}{" "}will be discarded</p> : null;
    $[23] = canStartCurrent;
    $[24] = controller.definition;
    $[25] = start;
    $[26] = view?.summary?.name;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== isLoadingColumns) {
    t15 = isLoadingColumns ? <p className="text-xs text-muted-foreground" id={LOADING_COLUMNS} role="status">Loading the table's columns…</p> : null;
    $[28] = isLoadingColumns;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t10 || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t15) {
    t16 = <div className="flex flex-col gap-3">{t8}{t10}{t12}{t13}{t14}{t15}</div>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== audience || $[37] !== controller || $[38] !== panel || $[39] !== start) {
    t17 = async details => {
      setIsSaving(true);
      const saved = await controller.saveAsNew({
        ...details,
        audience: toAudienceInput(audience)
      }, start).finally(() => setIsSaving(false));
      panel.replace("new", saved);
    };
    $[36] = audience;
    $[37] = controller;
    $[38] = panel;
    $[39] = start;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== audience) {
    t18 = <AudiencePicker onChange={setAudience} value={audience} data-sentry-element="AudiencePicker" data-sentry-source-file="saved-views-panel.tsx" />;
    $[41] = audience;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t17 || $[44] !== t18) {
    t19 = <ViewDetailsForm id={NEW_VIEW_FORM} onSubmit={t17} view={null} data-sentry-element="ViewDetailsForm" data-sentry-source-file="saved-views-panel.tsx">{t18}</ViewDetailsForm>;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t16 || $[47] !== t19) {
    t20 = <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3">{t16}{t19}</div>;
    $[46] = t16;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const t21 = isLoadingColumns ? LOADING_COLUMNS : undefined;
  const t22 = isSaving || isLoadingColumns;
  let t23;
  if ($[49] !== t21 || $[50] !== t22) {
    t23 = <footer className="flex justify-end border-t p-3"><Button aria-describedby={t21} disabled={t22} form={NEW_VIEW_FORM} type="submit" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">Save view</Button></footer>;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t20 || $[53] !== t23 || $[54] !== t7) {
    t24 = <>{t7}{t20}{t23}</>;
    $[52] = t20;
    $[53] = t23;
    $[54] = t7;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  return t24;
};
const ViewList = t0 => {
  const $ = _c(44);
  const {
    actions,
    children
  } = t0;
  const panel = useSavedViewsPanel();
  const controller = useTableConfiguration();
  const {
    definition
  } = controller;
  const {
    active,
    view
  } = useTableConfigurationSnapshot();
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] !== search) {
    t1 = search.trim();
    $[0] = search;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      wait: 300
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [debouncedSearch] = useDebouncedValue(t1, t2);
  const openId = active ? view?.id ?? null : null;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => setSearch(event.target.value);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== search) {
    t4 = <Input aria-label="Search Saved Views" autoFocus={true} className="h-8" onChange={t3} placeholder="Search views" value={search} data-sentry-element="Input" data-sentry-source-file="saved-views-panel.tsx" />;
    $[4] = search;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <X data-sentry-element="X" data-sentry-source-file="saved-views-panel.tsx" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== panel.close) {
    t6 = <Button aria-label="Close Saved Views" onClick={panel.close} size="icon" variant="ghost" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t5}</Button>;
    $[7] = panel.close;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <header className="flex items-center gap-2 border-b p-3">{t4}{t6}</header>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = active && view === null ? "page" : undefined;
  const t9 = active && view === null && "bg-muted";
  let t10;
  if ($[12] !== t9) {
    t10 = cn("w-full rounded px-2 py-1.5 text-left text-sm font-medium hover:bg-muted", t9);
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== actions) {
    t11 = () => actions.open(null);
    $[14] = actions;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== definition) {
    t12 = defaultViewName(definition);
    $[16] = definition;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t8) {
    t13 = <button aria-current={t8} className={t10} onClick={t11} type="button">{t12}</button>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t8;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  let t15;
  if ($[23] !== actions || $[24] !== debouncedSearch || $[25] !== openId) {
    t14 = <Section access="owned" actions={actions} openId={openId} search={debouncedSearch} title="My views" data-sentry-element="Section" data-sentry-source-file="saved-views-panel.tsx" />;
    t15 = <Section access="shared" actions={actions} openId={openId} search={debouncedSearch} title="Shared with me" data-sentry-element="Section" data-sentry-source-file="saved-views-panel.tsx" />;
    $[23] = actions;
    $[24] = debouncedSearch;
    $[25] = openId;
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t13 || $[29] !== t14 || $[30] !== t15) {
    t16 = <div className="min-h-0 flex-1 overflow-y-auto p-2">{t13}{t14}{t15}</div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== active || $[33] !== controller || $[34] !== panel) {
    t17 = () => {
      if (!active) {
        controller.visit(null);
      }
      panel.open("new");
    };
    $[32] = active;
    $[33] = controller;
    $[34] = panel;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Plus data-sentry-element="Plus" data-sentry-source-file="saved-views-panel.tsx" />;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t17) {
    t19 = <footer className="border-t p-2"><Button className="w-full justify-start" onClick={t17} size="sm" variant="ghost" data-sentry-element="Button" data-sentry-source-file="saved-views-panel.tsx">{t18} New view</Button></footer>;
    $[37] = t17;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== children || $[40] !== t16 || $[41] !== t19 || $[42] !== t7) {
    t20 = <>{t7}{children}{t16}{t19}</>;
    $[39] = children;
    $[40] = t16;
    $[41] = t19;
    $[42] = t7;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
};

/**
 * Saved Views browser docked beside the sidebar. A row opens its view; a view's page manages it.
 * Escape, Close or a press elsewhere in the app dismisses it.
 */
export const SavedViewsPanel = () => {
  const $ = _c(46);
  const panel = useSavedViewsPanel();
  const controller = useTableConfiguration();
  const {
    active,
    pinned
  } = useTableConfigurationSnapshot();
  const {
    isMobile,
    state
  } = useSidebar();
  const ref = useRef(null);
  const [error, setError] = useState(null);
  const page = panel.panel?.view ?? null;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = failure => setError(productError(failure));
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const fail = t0;
  let actions;
  if ($[1] !== active || $[2] !== controller || $[3] !== isMobile || $[4] !== panel || $[5] !== pinned) {
    let t1;
    if ($[7] !== pinned) {
      t1 = view => pinned.status === "ready" ? pinned.items.some(item => item.id === view.id) : view.is_pinned;
      $[7] = pinned;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== active || $[10] !== controller || $[11] !== isMobile || $[12] !== panel) {
      t2 = id => {
        setError(null);
        if (id === null) {
          if (active) {
            controller.openDefault();
          } else {
            controller.visit(null);
          }
        } else {
          if (active) {
            controller.open(id).catch(fail);
          } else {
            controller.visit(id);
          }
        }
        if (isMobile) {
          panel.close();
        }
      };
      $[9] = active;
      $[10] = controller;
      $[11] = isMobile;
      $[12] = panel;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    actions = {
      isPinned: t1,
      open: t2,
      togglePin: view_0 => {
        setError(null);
        const change = actions.isPinned(view_0) ? controller.unpin(view_0.id) : controller.pin(view_0.id);
        return change.catch(fail);
      }
    };
    $[1] = active;
    $[2] = controller;
    $[3] = isMobile;
    $[4] = panel;
    $[5] = pinned;
    $[6] = actions;
  } else {
    actions = $[6];
  }
  let t1;
  let t2;
  if ($[14] !== panel) {
    t1 = () => {
      const onPointerDown = event => {
        const {
          target
        } = event;
        if (!(target instanceof Element) || ref.current?.contains(target)) {
          return;
        }
        if (target.closest(`[${SAVED_VIEWS_TRIGGER}]`)) {
          return;
        }
        if (target.closest("[data-slot=\"sidebar\"], [data-slot=\"sidebar-inset\"]")) {
          panel.close();
        }
      };
      document.addEventListener("pointerdown", onPointerDown);
      return () => document.removeEventListener("pointerdown", onPointerDown);
    };
    t2 = [panel];
    $[14] = panel;
    $[15] = t1;
    $[16] = t2;
  } else {
    t1 = $[15];
    t2 = $[16];
  }
  useEffect(t1, t2);
  let t3;
  if ($[17] !== error) {
    t3 = error ? <p className="border-b px-3 py-2 text-xs text-destructive" role="alert">{error}</p> : null;
    $[17] = error;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const errorNote = t3;
  const t4 = isMobile && "inset-2";
  const t5 = !isMobile && "top-2 bottom-2";
  const t6 = !isMobile && (page ? "w-96" : "w-80");
  let t7;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6) {
    t7 = cn("fixed z-40 flex flex-col rounded-lg border bg-background shadow-lg", "transition-[opacity,translate] duration-200 ease-drawer starting:-translate-x-2 starting:opacity-0 motion-reduce:starting:translate-x-0", t4, t5, t6);
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== panel) {
    t8 = event_0 => {
      if (event_0.key === "Escape") {
        panel.close();
      }
    };
    $[23] = panel;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isMobile || $[26] !== state) {
    t9 = isMobile ? undefined : {
      left: state === "collapsed" ? "calc(var(--sidebar-width-icon) + 1.5rem)" : "calc(var(--sidebar-width) + 0.5rem)"
    };
    $[25] = isMobile;
    $[26] = state;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== page) {
    t10 = page === "new" ? <NewViewPage /> : null;
    $[28] = page;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== actions || $[31] !== errorNote || $[32] !== page || $[33] !== panel.panel?.section) {
    t11 = page !== null && page !== "new" ? <>{errorNote}<ViewPage actions={actions} section={panel.panel?.section} view={page} /></> : null;
    $[30] = actions;
    $[31] = errorNote;
    $[32] = page;
    $[33] = panel.panel?.section;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== actions || $[36] !== errorNote || $[37] !== page) {
    t12 = page === null ? <ViewList actions={actions}>{errorNote}</ViewList> : null;
    $[35] = actions;
    $[36] = errorNote;
    $[37] = page;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t13 = <aside aria-label="Saved Views" className={t7} onKeyDown={t8} ref={ref} style={t9} data-sentry-component="SavedViewsPanel" data-sentry-source-file="saved-views-panel.tsx">{t10}{t11}{t12}</aside>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
};

/** Renders the panel while it's open; mount it once inside `SavedViewsPanelProvider`. */
export const SavedViewsPanelHost = () => {
  return useSavedViewsPanel().panel ? <SavedViewsPanel /> : null;
};